import { PLATFORM_DEFAULTS } from '@plato/shared';
import { backendSnippets } from './backend.ts';
import { frontendSnippets } from './frontend.ts';

export interface EmbedSnippetInput {
  readonly publishableKey: string;
  readonly apiOrigin: string;
  readonly embedOrigin: string;
  readonly allowedOrigins: readonly string[];
  readonly mcpServers?: readonly string[];
  readonly sessionPath?: string;
}

export interface Snippet {
  readonly id: string;
  readonly title: string;
  readonly language: 'shell' | 'tsx' | 'ts' | 'js' | 'html' | 'python' | 'text';
  readonly code: string;
}

export interface EmbedSnippets {
  readonly install: Snippet;
  readonly frontend: readonly Snippet[];
  readonly backend: readonly Snippet[];
  readonly csp: Snippet;
  readonly notes: readonly string[];
}

export interface SnippetValues {
  readonly publishableKey: string;
  readonly mode: 'test' | 'live';
  readonly apiOrigin: string | null;
  readonly embedOrigin: string | null;
  readonly loaderUrl: string;
  readonly sessionsUrl: string;
  readonly sessionPath: string;
  readonly server: string | null;
}

export const DEFAULT_SESSION_PATH = '/api/plato/session';

export function embedSnippets(input: EmbedSnippetInput): EmbedSnippets {
  const values = checkedValues(input);
  const allowed = input.allowedOrigins.map((origin) => checkedOrigin(origin, 'allowedOrigins'));
  return {
    install: {
      id: 'install',
      title: 'Install',
      language: 'shell',
      code: 'npm install @plato/js @plato/node',
    },
    frontend: frontendSnippets(values),
    backend: backendSnippets(values),
    csp: {
      id: 'csp',
      title: 'Content-Security-Policy (only if your pages send one)',
      language: 'text',
      code: `script-src 'self' ${new URL(values.loaderUrl).origin};\nframe-src ${new URL(values.loaderUrl).origin};`,
    },
    notes: notes(values, allowed),
  };
}

function checkedValues(input: EmbedSnippetInput): SnippetValues {
  const key = /^plato_pk_(test|live)_\w{1,128}$/.exec(input.publishableKey);
  if (!key)
    throw new TypeError('publishableKey must look like plato_pk_test_... or plato_pk_live_...');
  const apiOrigin = checkedOrigin(input.apiOrigin, 'apiOrigin');
  const embedOrigin = checkedOrigin(input.embedOrigin, 'embedOrigin');
  const sessionPath = input.sessionPath ?? DEFAULT_SESSION_PATH;
  if (!/^\/[A-Za-z0-9/_.-]{0,200}$/.test(sessionPath)) {
    throw new TypeError('sessionPath must be a plain path such as /api/plato/session');
  }
  const server = input.mcpServers?.[0] ?? null;
  if (server !== null && !/^[A-Za-z0-9_-]{1,64}$/.test(server)) {
    throw new TypeError(`not an MCP server name: ${server}`);
  }
  return {
    publishableKey: input.publishableKey,
    mode: key[1] as 'test' | 'live',
    apiOrigin: apiOrigin === `https://${PLATFORM_DEFAULTS.apiHost}` ? null : apiOrigin,
    embedOrigin: embedOrigin === `https://${PLATFORM_DEFAULTS.embedHost}` ? null : embedOrigin,
    loaderUrl: `${embedOrigin}/plato.js`,
    sessionsUrl: `${apiOrigin}/v1/sessions`,
    sessionPath,
    server,
  };
}

function checkedOrigin(value: string, what: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new TypeError(`${what}: not an origin: ${value}`);
  }
  if (
    (url.protocol !== 'https:' && url.protocol !== 'http:') ||
    url.origin !== value.replace(/\/$/, '')
  ) {
    throw new TypeError(
      `${what}: expected a bare origin such as https://app.example.com, got ${value}`,
    );
  }
  return url.origin;
}

function notes(values: SnippetValues, allowed: readonly string[]): string[] {
  const out: string[] = [];
  out.push(
    allowed.length === 0
      ? 'No page origin is registered yet, so the assistant shows a blank frame everywhere. Ask Plato to register yours, including localhost for development.'
      : `The assistant renders only on pages at ${allowed.join(', ')}. Ask Plato to register any other origin, such as staging.`,
  );
  if (values.server === null) {
    out.push(
      'No data source is connected yet: the assistant chats and runs code, but cannot see your data. When your MCP server is registered, uncomment the server and token lines.',
    );
  }
  if (values.mode === 'test') {
    out.push('These are test keys. Live keys work the same way against your live tenant.');
  }
  out.push(
    "Keep PLATO_SECRET_KEY on your server. When a user signs out of your product, call plato('logout') (import { plato } from '@plato/js').",
  );
  return out;
}
